import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import TagTextFilter from '../../../../components/ui/filters/TagTextFilter';
import BzmIcon from '../../../../components/icons/BzmIcon';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import FullPageLoader from '../../../../components/loaders/FullPageLoader';
import acApi from '../../../../axios/ac/acApi';
import _ from 'lodash';
import { MockService } from '../../../../types/rest/mockServiceTypes';
import BlazeTable from '../../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from '../../../../components/blazeTable/BlazeTable.types';
import MockServiceDetail from '../MockServices/MockServiceDetail/MockServiceDetail';
import Status from '../../../../components/statusIndicator/Status';
import { filterListByKeyword } from '../../../../utils/filterUtils';
import { getPreferredEndpoint, getSelectedCountTitle } from '../../../../utils/miscUtil';
import Badges from '../../../../components/statusIndicator/Badges';
import BasePathInput from './inputs/BasePathInput';
import { MockServiceGroup } from '../../../../store/mockServiceGroups/mockServiceGroupTypes';
import AssignMockServiceModal from './modal/AssignMockServiceModal';
import EmptyGroupPlaceholder from './placeholder/EmptyGroupPlaceholder';
import { useDispatch, useSelector } from 'react-redux';
import { updateMockServiceGroupPatch } from '../../../../store/mockServiceGroups/mockServiceGroupsActions';
import ElevioTooltip from '../../../../components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import { showToastNotification } from '../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../store/ui/toasts/toastsTypes';
import { confirm } from '../../../../components/ui/modal/BlazeConfirmationModal';
import CopyEndpointPopover from '../../../../components/ui/CopyEndpointPopover/CopyEndpointPopover';
import { buildAnalyticsQueryForHits, updateMockServiceListWithHits } from '../../../../utils/mockServiceUtils';
import { getActiveWorkspaceId } from '../../../../utils/bzmUserConfigUtil';
import { getHitAndMissesFromAnalytics } from '../../../../store/analytics/data/analyticsDataActions';
import { Metrics } from '../../../../types/rest/analyticsTypes';
import HeaderCell from '../MockServices/Cells/HeaderCell';
import i18n from '../../../../i18n';
import StartMockBtn from '../../../../components/buttons/mockServiceButtons/StartMockBtn';
import {
  deployMockService,
  stopMockServiceThunk,
} from '../../../../store/mockServices/mockServiceList/mockServiceListActions';
import StopMockBtn from '../../../../components/buttons/mockServiceButtons/StopMockBtn';
import { Tracking } from '../../../../store/trackings/trackingsTypes';
import MixpanelService from '../../../../utils/mixpanelService';
import {
  ASSIGN_MOCK_SERVICE_TO_GROUP_OPEN_MODAL,
  DEASSIGN_MOCK_SERVICE_FROM_GROUP_BULK,
  DEASSIGN_MOCK_SERVICE_FROM_GROUP_SINGLE,
  RUN_MOCK_SERVICE_THROUGH_GROUP_VIEW,
  STOP_MOCK_SERVICE_THROUGH_GROUP,
} from '../../../../utils/mixpanelEvents';

interface GroupedMockServicesTableProps {
  group: MockServiceGroup;
  disabled: boolean;
}

const GroupedMockServicesTable = (props: GroupedMockServicesTableProps) => {
  const isMount = useIsMount();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mockServices, setMockServices] = useState<MockService[] | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [selectedMs, setSelectedMs] = useState<MockService[]>([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showAssignMockServiceModal, setShowAssignMockServiceModal] = useState(false);
  const trackings = useSelector((state: any) => state.trackings.trackings);

  useEffect(() => {
    if (isMount) {
      //Load virtual services on mount
      getMockServices();
    }
  }, []);

  //If virtual service assignment is changed from outside, reload
  useEffect(() => {
    if (!isMount && _.size(props.group.serviceMocks) !== _.size(mockServices)) {
      getMockServices(false);
    }
  }, [props.group.serviceMocks]);

  useEffect(() => {
    if (!isMount) {
      getMockServices(true);
      console.log('Trackings has changed...', trackings);
    }
  }, [trackings]);

  /**
   * Apply search filter to list of virtual services
   */
  const getFilteredMockServices = () => {
    if (_.isNil(mockServices)) {
      return [];
    }

    return filterListByKeyword(mockServices, search, ['name', 'serviceName']);
  };

  const isMockServiceExpanded = (msId: number) => {
    return expandedId === msId;
  };

  const filteredMockServices = getFilteredMockServices();

  const getBasePath = (mockService: MockService) => {
    const ms = _.find(props.group.serviceMocks, item => item.serviceMockId === mockService.id);
    if (_.isNil(ms)) {
      return '';
    } else {
      return ms.basePath;
    }
  };

  const getAnalytics = async (mockServices: MockService[]) => {
    //Get active workspace id
    const workSpaceId = getActiveWorkspaceId();
    const analyticsQuery = buildAnalyticsQueryForHits(mockServices, workSpaceId);
    const results: Metrics[] = await getHitAndMissesFromAnalytics(analyticsQuery);

    //Update mocks with analytics data
    const mocks = updateMockServiceListWithHits(mockServices, results);
    setMockServices(mocks);
  };

  /**
   * Gets list of Virtual Services from backend.
   * @param silentReload if true loading indicator is hidden
   */
  const getMockServices = async (silentReload?: boolean) => {
    if (silentReload !== true) {
      setIsLoading(true);
    }
    const groupId = props.group.id;
    const res = (await acApi.groups.getMockServices(groupId)).data.result;
    if (!_.isNil(res)) {
      const mocks = _.map(res, item => ({ ...item, metaData: { isDeployed: false } }));
      setMockServices(mocks);
      getAnalytics(mocks);
    } else {
      setMockServices([]);
    }

    if (silentReload !== true) {
      setIsLoading(false);
    }
  };

  const onChangeBasePathHandler = (basePathValue: string, mockId: number) => {
    const group = _.cloneDeep(props.group);

    //Find the existing definition of the base path
    const index = _.findIndex(props.group.serviceMocks, item => item.serviceMockId === mockId);
    if (index === -1) {
      group.serviceMocks.push({ serviceMockId: mockId, basePath: basePathValue });
    } else {
      group.serviceMocks[index].basePath = basePathValue;
    }

    dispatch(updateMockServiceGroupPatch(group, ['serviceMocks'], true));
  };

  const removeMockServices = async (mockServices: MockService[]) => {
    try {
      // Remove selection from the group
      const updatedGroup = _.cloneDeep(props.group);
      updatedGroup.serviceMocks = _.filter(updatedGroup.serviceMocks, item => {
        const index = _.findIndex(mockServices, sm => item.serviceMockId === sm.id);
        return index === -1;
      });
      await dispatch(updateMockServiceGroupPatch(updatedGroup, ['serviceMocks'], true));
      await getMockServices();
      dispatch(
        showToastNotification(
          ToastType.Success,
          `Selected Virtual Service${
            _.size(mockServices) === 1 ? ' has' : 's have'
          } been successfully removed from the group.`,
        ),
      );
    } catch (e) {
      dispatch(
        showToastNotification(ToastType.Danger, 'Selected Virtual Service(s) could not have been removed from group.'),
      );
    } finally {
      setSelectedMs([]);
    }
  };

  const removeMockServiceHandler = () => {
    const modalBody = (
      <div>
        Are you sure you want to remove <strong>{_.size(selectedMs)}</strong> selected Virtual Services from group?
      </div>
    );
    confirm({
      confirmation: modalBody,
      title: 'Remove Virtual Services from Group',
      yesOptionTitle: 'Remove',
    })
      .then(() => {
        MixpanelService.track(DEASSIGN_MOCK_SERVICE_FROM_GROUP_BULK());
        removeMockServices(selectedMs);
      })
      .catch(_.noop)
      .finally(() => {
        setSelectedMs([]);
      });
  };

  const getExistingPaths = (mockService: MockService) => {
    const { serviceMocks } = props.group;
    if (_.isNil(serviceMocks)) {
      return null;
    }

    //Filter out the current item and convert to array of strings
    return _.filter(serviceMocks, item => item.serviceMockId !== mockService.id).map(item => item.basePath);
  };

  const getBasePathCell = (mockService: MockService) => {
    const basePath = getBasePath(mockService);
    // Extract endpoint from group.endpoints (array of 1 or 0)
    const endpoint =
      Array.isArray(props.group.endpoints) && props.group.endpoints.length > 0 && props.group.endpoints[0]?.endpoint
        ? props.group.endpoints[0].endpoint
        : null;
    if (isMockServiceExpanded(mockService.id)) {
      return (
        <BasePathInput
          existingPaths={getExistingPaths(mockService)}
          displayCopyMessage={true}
          groupEndpointUrl={endpoint}
          value={basePath}
          onChange={value => onChangeBasePathHandler(value, mockService.id)}
          disabled={props.disabled}
        />
      );
    }

    //BasePath with copy icon
    return (
      <>
        {basePath}{' '}
        {props.group.status === 'RUNNING' && <CopyEndpointPopover endpoint={endpoint + basePath}></CopyEndpointPopover>}
      </>
    );
  };

  const count = getSelectedCountTitle(_.size(filteredMockServices), _.size(selectedMs));

  const bulkActions = (
    <button
      id={`qa-blk-ms-stop`}
      className="btn btn-link btn-no-hover p-0 ml-1"
      title={'Remove selected Virtual Services from group'}
      onClick={removeMockServiceHandler}
      disabled={_.size(selectedMs) === 0}
    >
      <BzmIcon variant={'icon-bzm-playlist-remove'} className={'icon-sm'} />
    </button>
  );

  const deployMockServiceHandler = (ms: MockService) => {
    //Mark this MS as configuring and start the deployment
    const msIndex = _.findIndex(mockServices, item => item.id === ms.id);
    const updatedList = _.cloneDeep(mockServices);

    MixpanelService.track(RUN_MOCK_SERVICE_THROUGH_GROUP_VIEW());

    if (msIndex !== -1) {
      updatedList[msIndex].status = 'CONFIGURING';
      setMockServices(updatedList);
    }

    //Start deployment process
    dispatch(deployMockService(ms));
  };

  /**
   * Checks tracking list if there is a tracking object with virtual service. If yes, returns
   * tracking status from there. If there is no tracking in redux, read status from entity.
   * @param ms
   */
  const getMockServiceStatus = (ms: MockService) => {
    if (_.size(trackings) === 0) {
      return ms.status;
    }

    const msTracking = _.find(
      trackings,
      (tracking: Tracking) => tracking.entityType === 'MOCK_SERVICE' && tracking.entityId === ms.id,
    );

    if (!_.isNil(msTracking)) {
      return 'CONFIGURING';
    }

    return ms.status;
  };

  const getMsActionsCell = (ms: MockService) => {
    const status = getMockServiceStatus(ms);
    return (
      <div>
        {status !== 'RUNNING' && (
          <StartMockBtn id={ms.id} disabled={status === 'CONFIGURING'} onClick={() => deployMockServiceHandler(ms)} />
        )}
        {status === 'RUNNING' && (
          <StopMockBtn
            id={ms.id}
            disabled={false}
            onStop={() => {
              MixpanelService.track(STOP_MOCK_SERVICE_THROUGH_GROUP());
              dispatch(stopMockServiceThunk(ms));
            }}
          />
        )}
        <div className={'flex-table-actions ml-3'}>
          <button
            className="btn btn-link-dark btn-no-hover"
            onClick={() => {
              MixpanelService.track(DEASSIGN_MOCK_SERVICE_FROM_GROUP_SINGLE());
              removeMockServices([ms]);
            }}
          >
            <BzmIcon variant={'icon-bzm-playlist-remove'} className={'icon-xs'} />
          </button>
        </div>
      </div>
    );
  };

  const header = (
    <Row>
      <Col lg={6} sm={12}>
        <h4 className={'text-uppercase'}>
          Virtual Services in this Group {count}
          <ElevioTooltip elevioId={'mock-service-groups-help'} />
          {bulkActions}
        </h4>
        <p>List of Virtual Services assigned to The Virtual Service Group.</p>
      </Col>
      <Col lg={6} sm={12}>
        <div className={'d-flex flex-row-reverse'}>
          <button
            className={'btn btn-primary ml-4'}
            onClick={() => {
              MixpanelService.track(ASSIGN_MOCK_SERVICE_TO_GROUP_OPEN_MODAL());
              setShowAssignMockServiceModal(true);
            }}
          >
            <BzmIcon variant={'icon-bzm-playlist-add'} size={16} className={'pr-2'} />
            <span className={'position-relative'} style={{ bottom: 2 }}>
              Assign Virtual Service
            </span>
          </button>

          <button
            className={'btn btn-secondary ml-4'}
            onClick={() => {
              getMockServices();
            }}
          >
            <BzmIcon variant={'icon-bzm-sync'} className={'pr-2'} /> Refresh
          </button>

          <div className={'d-inline-block position-relative'} style={{ top: '1px', minWidth: 290 }}>
            <TagTextFilter
              className={'ml-auto'}
              searchTerm={''}
              setSearchTerm={searchTerm => {
                setSearch(searchTerm);
              }}
              context={'mockServices'}
              placeholder={'Search by Name or Tag'}
            />
          </div>
        </div>
      </Col>
    </Row>
  );

  const cols: BlazeTableColDef[] = [
    {
      header: 'Name',
      accessor: 'name',
      isSortable: true,
      width: 300,
      isExpandLink: true,
    },
    {
      header: 'Service',
      accessor: 'serviceName',
      width: 120,
      headerComponent: (
        <HeaderCell
          title={i18n.t('mockServices:mockServicesTable.columnNames.service')}
          elevioHelpId={'Service_MockService_table'}
        />
      ),
      isSortable: true,
    },
    {
      header: 'Endpoint Base Path ',
      accessor: 'basePath',
      width: 300,
      cellComponent: (mockService: MockService) => getBasePathCell(mockService),
      isSortable: true,
      cellClass: 'flex-table-col-overflow-visible',
    },
    {
      header: 'Badges',
      accessor: 'badges',
      width: 120,
      cellComponent: (mockService: MockService) => {
        return <Badges mockService={mockService} />;
      },
      isSortable: true,
    },
    {
      header: 'Hits',
      accessor: 'hits',
      cellComponent: (mockService: MockService) => <span>{mockService.hits ? mockService.hits : '-'}</span>,
      width: 120,
      isSortable: true,
    },
    {
      header: 'Status',
      accessor: 'status',
      width: 120,
      cellComponent: (item: MockService) => {
        const status = getMockServiceStatus(item);
        return <Status status={status as any} id={item.id} />;
      },
      isSortable: true,
    },
    {
      header: 'Actions',
      accessor: 'id',
      cellComponent: (item: MockService) => getMsActionsCell(item),
      width: 120,
      isSortable: false,
    },
  ];

  const tableOptions: BlazeTableOptions = {
    minWidth: 900,
    pageable: true,
    selectable: true,
    showRowExpander: true,
    columnDef: cols,
    selectAllDropdown: true,
    expandDetailComponent: (item: MockService) => (
      <div className={'p-4'}>
        <MockServiceDetail rowData={item} saveServiceHandler={() => _.noop()} disabled={true} />
      </div>
    ),
  };

  const getEmptyPlaceholder = () => (
    <EmptyGroupPlaceholder
      onAssignMockService={() => {
        MixpanelService.track(ASSIGN_MOCK_SERVICE_TO_GROUP_OPEN_MODAL());
        setShowAssignMockServiceModal(true);
      }}
      disableAdd={_.isNil(props.group.shipId) || props.group.status === 'CONFIGURING'}
    />
  );

  return (
    <div className={'bg-light p-4'}>
      <div>
        {showAssignMockServiceModal && (
          <AssignMockServiceModal
            group={props.group}
            onHide={() => {
              setShowAssignMockServiceModal(false);
            }}
            onAssignmentFinished={msCount => {
              setShowAssignMockServiceModal(false);
              dispatch(
                showToastNotification(
                  ToastType.Success,
                  msCount + ' Virtual Services have been successfully assigned to the group.',
                ),
              );
              getMockServices();
            }}
          />
        )}

        {isLoading && _.isNil(mockServices) ? (
          <FullPageLoader
            style={{ minHeight: 300, paddingTop: 150 }}
            loadingText={'Loading Virtual Services in the Group...'}
          />
        ) : _.size(mockServices) === 0 ? (
          getEmptyPlaceholder()
        ) : (
          <>
            {header}
            <BlazeTable
              onSelectionChange={selectedItems => {
                setSelectedMs(selectedItems);
              }}
              expandedItem={expandedId}
              onExpandedItemChange={(id: number) => {
                if (expandedId === id) {
                  setExpandedId(null);
                } else {
                  setExpandedId(id);
                }
              }}
              data={filteredMockServices}
              isLoading={isLoading}
              options={tableOptions}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default GroupedMockServicesTable;
